
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Combines Tailwind CSS classes with clsx
 * 
 * Utility function for conditional class name application compatible with Tailwind
 * 
 * @param {...ClassValue[]} inputs - Class name inputs to combine
 * @returns {string} Combined class names optimized for Tailwind
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Attempts to parse JSON string safely
 * 
 * Wraps JSON.parse in a try-catch block to prevent errors from crashing the app
 * 
 * @param {string} jsonString - JSON string to parse
 * @returns {any | null} Parsed JSON object/array or null if parsing fails
 */
export function safeJsonParse(jsonString: string): any | null {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return null;
  }
}

/**
 * Generates a RFC4122 compliant UUID v4
 * 
 * @returns {string} A valid UUID v4 string
 */
export function generateUUID(): string {
  // Implementation based on RFC4122 version 4 UUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Validates if a string is a valid UUID
 * 
 * @param {string} uuid - String to validate as UUID
 * @returns {boolean} True if string is a valid UUID format
 */
export function isValidUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}
